<template>
  <vjs-modal v-bind="$options.propsData"
             :title="title"
             icon="la la-link"
             width="700"
             @opened="init">
    <template slot="body-content">
      <aside v-if="loaded" class="sync-feed-items-tag-modal">
        <div class="sync-feed-items-tag-list">
          <b-card v-for="sfi in staged_feed_items" :key="sfi.feed.uuid"
                  :header="`Feed: ${sfi.feed.name}`"
                  class="mt-4">
            <b-card-text>
              <b-row>
                <b-col cols="6">
                  <p><strong>Tags</strong></p>
                  <div v-if="sfi.feed.tags && sfi.feed.tags.length">
                    <multi-select-tag-bag :tags-list="sfi.feed.tags.map(tag => tag.name)" />
                  </div>
                  <div v-else>
                    Feed has no Tags; nothing to sync.
                  </div>
                </b-col>
                <b-col cols="6">
                  <collapse-text v-if="nullishChain(sfi, `staged_items.length`) > 0"
                                 :text="`Items to sync (${sfi.staged_items.length}):`" text-classes="font-weight-bold"
                                 :indent-content="false">
                    <ul class="clean-list mt-3">
                      <li v-for="item in sfi.staged_items" :key="item.uuid">
                        <a :href="`/items/${item.uuid}/edit`" target="_blank">
                          {{ item.name }}
                        </a>
                      </li>
                    </ul>
                  </collapse-text>
                  <div v-else>
                    No Items share tags with {{ sfi.feed.name }}; nothing to sync.
                  </div>
                </b-col>
              </b-row>
            </b-card-text>
          </b-card>
        </div>
      </aside>
      <aside v-else class="d-flex justify-content-center align-items-center">
        <b-spinner label="Fetching items..." />
      </aside>
    </template>
    <template slot="body-controls">
      <b-button variant="ss-standout" :disabled="waiting_callback" @click="() => close(true)">
        <loader v-if="waiting_callback" small class="mr-2" />Confirm
      </b-button>
      <b-button variant="ss-cancel" :disabled="waiting_callback" @click="() => close(false)">
        <loader v-if="waiting_callback" small class="mr-2" />Cancel
      </b-button>
    </template>
  </vjs-modal>
</template>

<script>
import VjsModal from 'VueModals/VjsModal';
import VjsModalMixin from 'VueModals/VjsModalMixin';
import MultiSelectTagBag from 'VueComponents/MultiSelectTagBag';
import CollapseText from 'VueComponents/CollapseText';

export default {
  name: `sync-tags-to-items-modal`,
  components: {
    VjsModal,
    MultiSelectTagBag,
    CollapseText,
  },
  mixins: [VjsModalMixin],
  props: {
    title: {
      type: String,
      required: false,
      default: `Syncing Items by Feed Tags:`,
    },
    feeds: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    staged_feed_items: null,
    loaded: false,
  }),
  methods: {
    /**
     * Initialises the modal state; manages the `loaded` state and triggers feed<->item reloading.
     */
    async init() {
      this.loaded = false;
      this.staged_feed_items = await this.loadFeedItems();
      this.loaded = true;
    },
    /**
     * Loads all items for all `feeds` via their tags.
     * Returns a single `Promise` which is the collection of all the API GET promises (or `null`).
     */
    async loadFeedItems() {
      try {
        return Promise.all(this.feeds.map(async (feed) => {
          if (!feed.tags?.length) {
            return {
              feed,
              staged_items: [],
            };
          }

          // if reload needed, reload; otherwise use already indexed
          const staged_items = (await axios.get(`/api/v1/items`, {
            params: {
              tags: feed.tags.map(tag => tag.name).join(`,`),
            },
          })).data.data;

          return {
            feed,
            staged_items,
          };
        }));
      } catch (err) {
        window.handleAxiosError(err);
        return null;
      }
    },
    /**
     * Closes this modal, and also clears the modal state to make next open look nicer.
     */
    close(confirmed) {
      this.staged_feed_items = null;
      this.loaded = false;
      this.close_data.confirm = confirmed;
      this.complete();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~AssetsSass/_variables" as *;

:deep(.vm--modal) {
  .vjs-modal-body {
    overflow: auto;
  }
}

.sync-feed-items-tag-modal {
  max-height: 70vh;
  display: flex;
  flex-direction: column;

  .exit-buttons {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    padding: 10px;
    border-top: 1px solid;
    border-image-source: linear-gradient(to right, transparent, map.get($ss-adds, 'light-grey'), transparent);
    border-image-slice: 1;
  }

  .card-header {
    font-weight:bold;
  }

  .collapse-text {
    a:first-child {
      font-weight:bold;
    }
  }
}
</style>
